import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { CheckoutActions } from "../actions";
import { ApiService } from "../../../../SGRE-shared/services/api.service";
import { switchMap, map, catchError, of, mergeMap, concatMap, takeUntil, groupBy, exhaustMap } from "rxjs";
import { GlobalMessageService, LoggerService, tryNormalizeHttpError } from "@spartacus/core";
import { pDropdown } from "../../../../SGRE-shared/models/primeng.model";
import { CheckoutDeliveryModesConnector } from "@spartacus/checkout/base/core";
import { GlobalService } from "../../../../SGRE-shared/services/global.service";
import { CheckoutCartsEffectsService } from "./checkout-carts-effects.service";
import { CheckoutCartsState } from "../checkout.state";
import { DeliveryMode } from "@spartacus/cart/base/root";
import { CheckoutCartDetails } from "../../models/checkout.model";

@Injectable()
export class CheckoutShippingMethodEffects {

    protected logger = inject(LoggerService);
    private pendingShippingMethodRequests = 0;

    constructor(
        private actions$: Actions,
        private apiService: ApiService,
        private checkoutDeliveryModesConnector: CheckoutDeliveryModesConnector,
        private globalService: GlobalService,
        private checkoutCartsEffectsService: CheckoutCartsEffectsService,
    ) { }

    resetCounter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.clearCheckoutState),
            map(() => {
                this.pendingShippingMethodRequests = 0;
                return { type: '[Checkout] Counter Reset Complete' };
            })
        ),
        { dispatch: false }
    );

    loadShippingMethod$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.loadShippingMethod),
            groupBy(action => action.cartId), // Group by cartId to prevent duplicate calls
            mergeMap(group$ =>
                group$.pipe(
                    exhaustMap(({ cartId, accordionType }) => { // exhaustMap ignores new requests while one is pending
                        this.pendingShippingMethodRequests++;
                        return this.checkoutDeliveryModesConnector.getSupportedModes(this.globalService.getUserId(), cartId)
                            .pipe(
                                takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                                map((data: DeliveryMode[]) => {
                                    let deliveryModeArr: pDropdown[] = [];
                                    if (data?.length > 0) {
                                        for (const item of data) {
                                            let obj: pDropdown = { label: item?.name, value: item, id: item.code }
                                            deliveryModeArr.push(obj);
                                        }
                                    };
                                    this.pendingShippingMethodRequests--;
                                    if (this.pendingShippingMethodRequests === 0) {
                                        this.globalService.loadingSubject.next(false);
                                    }
                                    return CheckoutActions.loadShippingMethodSuccess({ payload: deliveryModeArr, accordionType });
                                }),
                                catchError(error => {
                                    this.pendingShippingMethodRequests--;
                                    if (this.pendingShippingMethodRequests === 0) {
                                        this.globalService.loadingSubject.next(false);
                                    }
                                    return of(CheckoutActions.loadShippingMethodFail({
                                        payload: tryNormalizeHttpError(error, this.logger),
                                        accordionType
                                    }))
                                })
                            );
                    })
                )
            )
        )
    );

    updateShippingMethod$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.updateShippingMethod),
            switchMap(({ cartId, codeVal, accordionType, orderType, legalEntityId }) => {
                return this.apiService.putDeliveryMethod(cartId, codeVal)
                    .pipe(
                        takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                        concatMap(() => {
                            let paramsObject = {
                                fields: 'FULL',
                                legalEntityId
                            }
                            return this.apiService.getCartsList(cartId, paramsObject)
                                .pipe(
                                    takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                                    map((checkoutCartsVal: CheckoutCartDetails) => {
                                        const checkoutCartsData: CheckoutCartsState = this.checkoutCartsEffectsService.modifyCartData(checkoutCartsVal, orderType);
                                        return CheckoutActions.updateShippingMethodSuccess({
                                            checkoutCartsData,
                                            accordionType
                                        });
                                    })
                                )
                        }),
                        catchError(error => of(CheckoutActions.updateShippingMethodFail({
                            payload: tryNormalizeHttpError(error, this.logger),
                            accordionType
                        })))
                    );
            }),
        )
    );
}
